import { ArrowDownIcon, ArrowUpIcon, ListFilterIcon, ListIcon, SearchIcon } from "lucide-react"

import { LogoIcon } from "@/components/LogoIcon"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group"
import { TalentFilterSheet } from "@/components/filters/TalentFilterSheet"
import { countActiveFilters } from "@/components/talentBrowse/lib/filter"
import { sortOptionPlatform, type SortOption } from "@/components/talentBrowse/lib/sort"
import type { BrowseFilter, Currency, List, Platform } from "@/shared/types"

export type TalentBrowseToolbarProps = {
    search: string
    onSearchChange: (value: string) => void
    lists: List[]
    listId: string
    onListIdChange: (listId: string) => void
    sortOptions: SortOption[]
    sortBy: string
    onSortByChange: (sortBy: string) => void
    filters?: {
        open: boolean
        onOpenChange: (open: boolean) => void
        value: BrowseFilter
        onChange: (value: BrowseFilter) => void
        platforms: Platform[]
        showPriceChips?: boolean
        currency?: Currency
        countryList?: string[]
        tagList?: string[]
        cityList?: string[]
        disabled?: boolean
    }
}

/** Search, list switcher, sort, and Filters (badge = active chip count). */
export function TalentBrowseToolbar({
    search,
    onSearchChange,
    lists,
    listId,
    onListIdChange,
    sortOptions,
    sortBy,
    onSortByChange,
    filters,
}: TalentBrowseToolbarProps) {
    const currentList = lists.find((list) => list.id === listId)
    const listLabel = currentList?.name || "All"
    const currentSort = sortOptions.find((option) => option.value === sortBy)
    const filterCount = filters ? countActiveFilters(filters.value, Boolean(filters.showPriceChips)) : 0

    return (
        // Search grows; list / sort / filter stay on one toolbar row.
        <div className="mb-4 flex flex-wrap items-center gap-2 [anchor-name:--talent-browse-toolbar]">
            <InputGroup className="min-w-48 flex-1 bg-white">
                <InputGroupAddon>
                    <SearchIcon />
                </InputGroupAddon>
                <InputGroupInput
                    value={search}
                    onChange={(event) => onSearchChange(event.target.value)}
                    placeholder="Search Creators"
                    aria-label="Search creators"
                />
            </InputGroup>
            <DropdownMenu>
                <DropdownMenuTrigger render={<Button type="button" variant="outline" />}>
                    <ListIcon data-icon="inline-start" />
                    {listLabel}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-auto min-w-48">
                    {lists.map((list) => (
                        <DropdownMenuItem key={list.id || "all"} onClick={() => onListIdChange(list.id || "")}>
                            {list.name || "All"}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
                <DropdownMenuTrigger render={<Button type="button" variant="outline" />}>
                    <SortOptionVisual option={currentSort} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-auto min-w-52">
                    <DropdownMenuRadioGroup value={sortBy} onValueChange={onSortByChange}>
                        {sortOptions.map((option) => (
                            <DropdownMenuRadioItem key={option.value} value={option.value}>
                                <SortOptionVisual option={option} />
                            </DropdownMenuRadioItem>
                        ))}
                    </DropdownMenuRadioGroup>
                </DropdownMenuContent>
            </DropdownMenu>
            {filters ? (
                <Button type="button" variant="outline" className="relative" onClick={() => filters.onOpenChange(true)}>
                    <ListFilterIcon data-icon="inline-start" />
                    Filter
                    {filterCount > 0 ? (
                        // Count hangs off the Filter corner (Figma 322:24545); it is not in-flow next to the label.
                        <Badge variant="blue" className="absolute -top-2 -right-3 pointer-events-none">
                            {filterCount}
                        </Badge>
                    ) : null}
                </Button>
            ) : null}
            {filters ? (
                <TalentFilterSheet
                    open={filters.open}
                    onOpenChange={filters.onOpenChange}
                    value={filters.value}
                    onChange={filters.onChange}
                    platforms={filters.platforms}
                    showPriceChips={filters.showPriceChips}
                    currency={filters.currency}
                    countryList={filters.countryList}
                    tagList={filters.tagList}
                    cityList={filters.cityList}
                    disabled={filters.disabled}
                />
            ) : null}
        </div>
    )
}

function SortOptionVisual({ option }: { option: SortOption | undefined }) {
    if (!option) {
        return (
            <>
                <ArrowDownIcon data-icon="inline-start" />
                Sort
            </>
        )
    }

    const platform = sortOptionPlatform(option)

    return (
        <>
            {platform ? <LogoIcon brand={platform} className="size-4" /> : null}
            {option.name}
            {option.direction === "desc" ? (
                <ArrowDownIcon className="size-4 text-success! **:text-success!" />
            ) : (
                <ArrowUpIcon className="size-4 text-orange-500! **:text-orange-500!" />
            )}
        </>
    )
}
